import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Shield, FileText } from 'lucide-react';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const PropelLogo = ({ className = 'w-8 h-8' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" fill="none" className={className}>
    <path d="M6 26L14 6L17 15L20 6L28 26L20 21L17 26L14 21Z" fill="#4F46E5" opacity="0.95"/>
    <path d="M14 6L17 15L20 6L17 10Z" fill="#6366F1"/>
  </svg>
);

export default function ContactPage() {
  useDocumentTitle('Contact - Propel');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-10">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <PropelLogo className="w-8 h-8" />
            <span className="text-xl font-heading font-bold text-gray-900">Propel</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-gray-900">Contact Us</h1>
          <p className="mt-2 text-gray-600">We'd love to hear from you. Reach out for any of the following.</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 sm:p-10">
          {/* Email */}
          <div className="flex items-start gap-4 mb-8">
            <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0">
              <Mail className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900 mb-1">Email</h2>
              <a
                href="mailto:contact@emerssive.com"
                className="text-primary-600 hover:text-primary-700 transition-colors"
              >
                contact@emerssive.com
              </a>
            </div>
          </div>

          {/* What to contact about */}
          <div className="border-t border-gray-100 pt-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">How can we help?</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-primary-400 mt-2 flex-shrink-0" />
                <div>
                  <p className="font-medium text-gray-800">General Support</p>
                  <p className="text-sm text-gray-500">Questions about using Propel, troubleshooting, or feature requests.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-primary-400 mt-2 flex-shrink-0" />
                <div>
                  <p className="font-medium text-gray-800">Account & Data Requests</p>
                  <p className="text-sm text-gray-500">Account deletion, data export, or exercising your privacy rights.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-primary-400 mt-2 flex-shrink-0" />
                <div>
                  <p className="font-medium text-gray-800">Security Concerns</p>
                  <p className="text-sm text-gray-500">Report a vulnerability or security issue. We take these seriously and will respond promptly.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-primary-400 mt-2 flex-shrink-0" />
                <div>
                  <p className="font-medium text-gray-800">Partnership & Business Inquiries</p>
                  <p className="text-sm text-gray-500">Interested in working with us or integrating with Propel.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Legal links */}
        <div className="mt-8 flex items-center justify-between text-sm text-gray-400">
          <p>&copy; {new Date().getFullYear()} Propel. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="hover:text-gray-600 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-gray-600 transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
